<script setup>
  import { computed } from 'vue'
  import BcsWizardSteps from '../common/BcsWizardSteps.vue'

  /**
   * BcstQuizStepper — domain wrapper over BcsWizardSteps for quiz question navigation.
   *
   * Each step label shows "N. [question text truncated]"; the native browser tooltip
   * on hover reveals the full question text. Hover color effect (primary border + text)
   * is applied via :deep() so BcsWizardSteps stays unmodified.
   *
   * Quiz states → BcsWizardSteps states:
   *   done       (submitted)               → completed
   *   answered   (draft answer, in-flight)   → completed
   *   active     (current question)         → active
   *   unanswered (not yet reached)          → empty
   */
  const props = defineProps({
    questions: { type: Array, default: () => [] },
    answers: { type: Object, default: () => ({}) },
    submittedQuestionIds: { type: [Set, Array], default: () => new Set() },
    currentIndex: { type: Number, default: 0 },
    isCompleted: { type: Boolean, default: false },
  })

  const emit = defineEmits(['navigate'])

  const STATUS_MAP = {
    done: 'completed',
    active: 'active',
    answered: 'completed',
    unanswered: 'empty',
  }

  function isSubmitted(questionId) {
    const submitted = props.submittedQuestionIds
    if (submitted instanceof Set) return submitted.has(questionId)
    return Array.isArray(submitted) && submitted.includes(questionId)
  }

  function quizStepStatus(question, index) {
    const answer = props.answers[question.id]
    // Multiple-choice answers are arrays; an empty array is not "answered".
    const hasAnswer = Array.isArray(answer) ? answer.length > 0 : answer != null
    if (props.isCompleted) return hasAnswer ? 'done' : 'unanswered'
    if (index === props.currentIndex) return 'active'
    if (isSubmitted(question.id)) return 'done'
    if (hasAnswer) return 'answered'
    return 'unanswered'
  }

  const wizardSteps = computed(() =>
    props.questions.map((q, i) => ({
      title: `Question ${i + 1}`,
      status: STATUS_MAP[quizStepStatus(q, i)],
    }))
  )

  // Cap the stepper width so steps don't spread across the full container.
  // Formula: N circles (32px) + (N-1) connectors (~112px each, from Figma).
  const stepperMaxWidth = computed(() => {
    const n = props.questions.length
    if (n < 2) return '100%'
    return `${n * 32 + (n - 1) * 112}px`
  })
</script>

<template>
  <div class="bcst-quiz-stepper" :style="{ maxWidth: stepperMaxWidth }">
    <BcsWizardSteps
      :steps="wizardSteps"
      :activeStep="currentIndex"
      :showNumber="false"
      labelAlign="center"
      :showProgressLine="false"
      :clickableSteps="true"
      @step-clicked="(i) => emit('navigate', i)" />
  </div>
</template>

<style scoped>
  .bcst-quiz-stepper {
    width: 100%;
    padding-top: 6px;
    overflow: visible;
  }

  :deep(.wizard-step) {
    cursor: pointer;
  }

  :deep(.wizard-step:hover .circle) {
    border-color: var(--bs-primary);
  }

  :deep(.wizard-step:hover .step-text) {
    color: var(--bs-primary);
  }

  /* In the quiz stepper only the active step should use the primary accent;
     answered/completed questions are left to BcsWizardSteps' standard styles
     so they display the checkmark icon. */
  :deep(.wizard-step:not(.active) .step-text) {
    color: #6C757D;
    font-weight: 400;
  }

  :deep(.wizard-step:not(.active).completed .step-text) {
    color: var(--bs-primary);
    font-weight: 700;
  }

  :deep(.wizard-step:not(.active).empty .circle) {
    background-color: #ffffff;
    border-color: var(--bs-gray-300);
    color: #6C757D;
  }

  :deep(.wizard-step:not(.active).empty .circle img) {
    visibility: hidden;
  }
</style>
